import React, { useEffect, useEffectEvent, useMemo, Suspense } from "react";
import SectionsRenderer from "@/components/landingPage/common/SectionsRenderer";
import NoContentAvailableState from "@/components/landingPage/common/NoContentAvailableState";
// Import animationConfig to ensure MotionGlobalConfig is set before any motion components render
import "@/config/animationConfig";
import {
  LandingPageLanguageProvider,
  LandingPageLanguageLoader,
  useLandingPageLanguage,
} from "@/components/providers/LandingPageLanguageProvider";
import { LandingPageDirectionProvider } from "@/components/providers/LandingPageDirectionProvider";
import {
  LandingPageThemeProvider,
  useLandingPageTheme,
} from "@/components/providers/LandingPageThemeProvider";
import { LandingPageContentProvider } from "@/components/providers/LandingPageContentProvider";
import { SiteConfigProvider } from "@/contexts/SiteConfigContext";
import type { Language, LanguageConfig } from "@/types/theme";
import type { SiteConfig } from "@/types/sites";
import { buildSupportedLanguages } from "@/utils/languageUtils";
import { ensureCompleteTheme } from "@/utils/themeUtils";
import { defaultConfig } from "@/components/landingPage/config";
import { getPaletteClass } from "@/utils/paletteUtils";
import { buildTypographyCssVars } from "@/utils/typographyTokens";
import { FontPreloader } from "@/components/shared/FontPreloader";
import { useAnalytics } from "@/analytics";
import { PreviewModeProvider } from "@/contexts/PreviewModeContext";

// Defer header and footer to separate chunks to minimize initial JS for public apps
const LazyHeaderSection = React.lazy(
  () => import("@/components/landingPage/header"),
);
const LazyFooter = React.lazy(() => import("@/components/landingPage/footer"));
import GenLPBadge from "@/components/shared/GenLPBadge";

interface LandingPageRendererContentProps {
  config: SiteConfig;
  content: any;
  availableLanguages: string[];
  onLanguageChange?: (language: string) => void;
  className?: string;
  style?: React.CSSProperties;
  enableTheme?: boolean;
  enableDarkMode?: boolean;
  onDarkModeChange?: (isDark: boolean) => void;
  showDarkModeIndicator?: boolean;
  preventGlobalDarkMode?: boolean; // Prevent applying dark class to document root
  children?: React.ReactNode; // For custom content like InvitationBanner
  onFirstSectionReady?: () => void; // Notifies when the first visible section (e.g., hero) has mounted
  debugSection?: string | null; // When set, render only this single section (for debugging)
  skipSections?: string[]; // Sections to skip (rendered elsewhere, e.g., server-side)
}

const LandingPageRendererContent: React.FC<LandingPageRendererContentProps> = ({
  config,
  content,
  availableLanguages,
  onLanguageChange,
  className = "",
  style,
  enableTheme = false,
  enableDarkMode = false,
  preventGlobalDarkMode = false,
  children,
  onFirstSectionReady,
  debugSection,
  skipSections = [],
}) => {
  const { currentLanguage, changeLanguage } = useLandingPageLanguage();
  const themeContext = useLandingPageTheme();
  const isDarkMode = enableTheme ? themeContext?.isDarkMode : enableDarkMode;
  const { track } = useAnalytics();

  // Prefetch hero block chunk to improve LCP — loads the specific block variant
  useEffect(() => {
    const heroBlockType =
      config.layout?.hero?.blockConfig?.blockType || "classic-centered";
    // Map block types to their chunk imports (mirrors hero/index.tsx loaders)
    const heroBlockImports: Record<string, () => Promise<unknown>> = {
      "split-hero": () =>
        import("@/components/landingPage/hero/blocks/SplitHeroBlock"),
      "split-hero-color": () =>
        import("@/components/landingPage/hero/blocks/SplitHeroColorBlock"),
      "centered-hero": () =>
        import("@/components/landingPage/hero/blocks/CenteredHeroBlock"),
      "creative-portfolio": () =>
        import("@/components/landingPage/hero/blocks/CreativePortfolioBlock"),
      "app-download": () =>
        import("@/components/landingPage/hero/blocks/AppDownloadHeroBlock"),
      "saas-tech-stack": () =>
        import("@/components/landingPage/hero/blocks/SaasTechStackBlock"),
      "layered-insight": () =>
        import("@/components/landingPage/hero/blocks/LayeredInsightHeroBlock"),
      "stellar-canvas": () =>
        import("@/components/landingPage/hero/blocks/StellarCanvasHeroBlock"),
      "nebula-bento": () =>
        import("@/components/landingPage/hero/blocks/NebulaBentoHeroBlock"),
      "geometric-abstract": () =>
        import("@/components/landingPage/hero/blocks/GeometricAbstractHeroBlock"),
      "creative-bold": () =>
        import("@/components/landingPage/hero/blocks/CreativeBoldHeroBlock"),
      monolith: () =>
        import("@/components/landingPage/hero/blocks/HeroMonolithBlock"),
      "monolith-split": () =>
        import("@/components/landingPage/hero/blocks/HeroMonolithSplitBlock"),
      editorial: () =>
        import("@/components/landingPage/hero/blocks/HeroEditorialBlock"),
      "editorial-split": () =>
        import("@/components/landingPage/hero/blocks/HeroEditorialSplitBlock"),
      bento: () =>
        import("@/components/landingPage/hero/blocks/HeroBentoBlock"),
      aura: () => import("@/components/landingPage/hero/blocks/HeroAuraBlock"),
      "aura-split": () =>
        import("@/components/landingPage/hero/blocks/HeroAuraSplitBlock"),
      lab: () => import("@/components/landingPage/hero/blocks/HeroLabBlock"),
      "lab-split": () =>
        import("@/components/landingPage/hero/blocks/HeroLabSplitBlock"),
      kinetic: () =>
        import("@/components/landingPage/hero/blocks/HeroKineticBlock"),
      "kinetic-split": () =>
        import("@/components/landingPage/hero/blocks/HeroKineticSplitBlock"),
      cinematic: () =>
        import("@/components/landingPage/hero/blocks/HeroCinematicBlock"),
      "cinematic-split": () =>
        import("@/components/landingPage/hero/blocks/HeroCinematicSplitBlock"),
      swiss: () =>
        import("@/components/landingPage/hero/blocks/HeroSwissBlock"),
      "swiss-split": () =>
        import("@/components/landingPage/hero/blocks/HeroSwissSplitBlock"),
      "neo-brutal": () =>
        import("@/components/landingPage/hero/blocks/HeroNeoBrutalBlock"),
      atelier: () =>
        import("@/components/landingPage/hero/blocks/HeroAtelierBlock"),
      authority: () =>
        import("@/components/landingPage/hero/blocks/HeroAuthorityBlock"),
      vital: () =>
        import("@/components/landingPage/hero/blocks/HeroVitalBlock"),
      trade: () =>
        import("@/components/landingPage/hero/blocks/HeroTradeBlock"),
      active: () =>
        import("@/components/landingPage/hero/blocks/HeroActiveBlock"),
      "classic-centered": () =>
        import("@/components/landingPage/hero/blocks/ClassicCenteredBlock"),
    };
    const loader =
      heroBlockImports[heroBlockType] ?? heroBlockImports["classic-centered"];
    // Also prefetch the hero section wrapper
    Promise.all([import("@/components/landingPage/hero"), loader()]).catch(
      () => {},
    );
  }, [config.layout?.hero?.blockConfig?.blockType]);

  // Resolve content based on current language
  const resolvedContent = useMemo(() => {
    // If content is already flat (single language content)
    if (content && typeof content === "object" && !Array.isArray(content)) {
      const contentKeys = Object.keys(content);
      if (contentKeys.length === 0) {
        return {};
      }

      // Check if it looks like multi-language content
      const firstKey = contentKeys[0];
      if (firstKey && availableLanguages.includes(firstKey)) {
        // Multi-language content - select current language content
        if (content[currentLanguage]) {
          return content[currentLanguage];
        }
        // Fallback to first available language
        const firstAvailableLang = availableLanguages[0];
        if (firstAvailableLang && content[firstAvailableLang]) {
          return content[firstAvailableLang];
        }
        return {};
      } else {
        // Single language content
        return content;
      }
    }
    return content || {};
  }, [content, currentLanguage, availableLanguages]);
  // Apply dark class to document root if needed (unless prevented)
  useEffect(() => {
    if (enableDarkMode && !preventGlobalDarkMode) {
      const htmlElement = document.documentElement;

      if (isDarkMode) {
        htmlElement.classList.add("dark");
      } else {
        htmlElement.classList.remove("dark");
      }

      return () => {
        htmlElement.classList.remove("dark");
      };
    }
  }, [enableDarkMode, isDarkMode, preventGlobalDarkMode]);

  // When preventing global dark mode, explicitly neutralize any existing global dark on <html>
  // so that Tailwind dark: variants don't leak from outer apps/providers.
  useEffect(() => {
    if (!preventGlobalDarkMode) return;
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    const hadLight = root.classList.contains("light");

    // Remove global dark to ensure isolated theming inside the container
    root.classList.remove("dark");
    // Ensure a neutral/light baseline to avoid unintended styles
    if (!hadLight) root.classList.add("light");

    return () => {
      // Restore previous classes
      root.classList.remove("light");
      if (hadDark) root.classList.add("dark");
      if (hadLight) root.classList.add("light");
    };
  }, [preventGlobalDarkMode]);

  const headerSupportedLanguages = useMemo(() => {
    return resolvedContent?.header?.supportedLanguages;
  }, [resolvedContent]);

  const supportedLanguages = useMemo(() => {
    return buildSupportedLanguages(
      availableLanguages,
      headerSupportedLanguages,
    );
  }, [availableLanguages, headerSupportedLanguages]);

  // CRITICAL FIX: Pre-compute header data to avoid object recreation
  const headerData = useMemo(() => {
    return {
      ...resolvedContent?.header,
      supportedLanguages: supportedLanguages,
    };
  }, [resolvedContent?.header, supportedLanguages]);

  const getOrderedSections = useMemo(() => {
    const defaultOrder = [
      "hero",
      "about",
      "experience",
      "skills",
      "projects",
      "contact",
      "social",
      "features",
      "testimonials",
      "faq",
      "pricing",
      "team",
      "services",
      "gallery",
      "timeline",
    ];

    let sections: string[];
    if (config.sectionOrder && config.sectionOrder.length > 0) {
      sections = config.sectionOrder;
    } else {
      sections = defaultOrder;
    }

    // When debugSection is set, filter to show only that section
    if (debugSection) {
      return [debugSection];
    }

    return sections;
  }, [config.sectionOrder, debugSection]);

  // CRITICAL FIX: Stabilize language change handler
  const handleLanguageChange = React.useCallback(
    (language: string) => {
      if (availableLanguages.includes(language)) {
        // Update language provider if supported by UI
        changeLanguage(language as Language);

        // Call external handler if provided
        if (onLanguageChange) {
          onLanguageChange(language);
        }
      } else {
        console.warn(`Language '${language}' is not available in this version`);
      }
    },
    [availableLanguages, changeLanguage, onLanguageChange],
  );

  // Font loading is now handled by FontPreloader component

  // Stabilize analytics callbacks so the effects below don't re-subscribe
  // listeners every time `track` reference changes.
  const onScrollThreshold = useEffectEvent((threshold: number) => {
    track.trackScroll(threshold);
  });
  const onWebVital = useEffectEvent(
    (metric: { name: string; value: number }) => {
      track.trackWebVital(metric);
    },
  );
  const onLinkClick = useEffectEvent((params: Record<string, unknown>) => {
    track.trackEvent(
      "click_link",
      params as Record<string, string | number | boolean | undefined>,
    );
  });

  // Scroll depth tracking (25/50/75/90%)
  useEffect(() => {
    const thresholds = [25, 50, 75, 90];
    const fired = new Set<number>();
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = doc.scrollTop || document.body.scrollTop;
      const height = doc.scrollHeight - doc.clientHeight;
      if (height <= 0) return;
      const percent = Math.min(100, Math.round((scrolled / height) * 100));
      for (const t of thresholds) {
        if (percent >= t && !fired.has(t)) {
          fired.add(t);
          onScrollThreshold(t);
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll as any);
  }, [onScrollThreshold]);

  // Web Vitals tracking
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mod: any = await import("web-vitals");
        const report = (m: any) => {
          if (!cancelled) onWebVital({ name: m.name, value: m.value });
        };
        mod.onCLS(report);
        mod.onLCP(report);
        if (typeof mod.onINP === "function") {
          mod.onINP(report);
        } else if (typeof mod.onFID === "function") {
          mod.onFID(report);
        }
        mod.onTTFB(report);
      } catch (e) {
        // web-vitals not available or failed to load; ignore silently
        void e;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onWebVital]);

  // Automatic link tracking
  useEffect(() => {
    const isExternalLink = (href: string) => {
      try {
        const parsed = new URL(href, window.location.href);
        return parsed.hostname !== window.location.hostname;
      } catch {
        return false;
      }
    };

    const handleLinkClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest("a");
      if (!target) return;

      const href = target.getAttribute("href");
      if (!href) return;

      const isExternal = isExternalLink(href);
      const isEmail = href.startsWith("mailto:");
      const isPhone = href.startsWith("tel:");
      const isDownload = target.hasAttribute("download");

      onLinkClick({
        link_url: href,
        link_text: target.textContent?.slice(0, 100) || "Link",
        is_external: isExternal,
        is_email: isEmail,
        is_phone: isPhone,
        is_download: isDownload,
      });
    };

    document.addEventListener("click", handleLinkClick, true);
    return () => document.removeEventListener("click", handleLinkClick, true);
  }, [onLinkClick]);

  const typographyVars = useMemo(
    () =>
      buildTypographyCssVars(
        config.typography?.scale ?? 1,
        config.typography?.bodyFont,
        config.typography?.headingFont,
      ),
    [
      config.typography?.scale,
      config.typography?.bodyFont,
      config.typography?.headingFont,
    ],
  );

  const containerStyle = useMemo(
    () => ({
      ...typographyVars,
      ...style,
    }),
    [typographyVars, style],
  );

  const direction =
    supportedLanguages.find(
      (lang: LanguageConfig) => lang.code === currentLanguage,
    )?.direction || "ltr";

  // Check if we have any meaningful content to render
  if (!resolvedContent || Object.keys(resolvedContent).length === 0) {
    return (
      <NoContentAvailableState
        currentLanguage={currentLanguage}
        availableLanguages={availableLanguages}
      />
    );
  }
  // Use palette to set a sensible base background for the whole page/container
  const containerBgClass = getPaletteClass(
    (config as SiteConfig).theme,
    "backgroundPrimary",
    "bg-white dark:bg-gray-900",
  );

  return (
    <div
      className={`min-h-screen landing-page-content ${containerBgClass} ${className} ${isDarkMode ? "dark" : ""}`}
      style={containerStyle}
      dir={direction}
    >
      {/* Custom content (e.g., InvitationBanner) */}
      {children}

      {/* Header - hidden in debug section mode or if in skipSections */}
      {config.layout?.header &&
        !debugSection &&
        !skipSections.includes("header") && (
          <Suspense fallback={null}>
            <LazyHeaderSection
              data={headerData}
              config={config}
              assets={resolvedContent.assets}
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
            />
          </Suspense>
        )}

      {/* Sections */}
      <LandingPageContentProvider content={resolvedContent}>
        <SectionsRenderer
          orderedSections={getOrderedSections}
          content={resolvedContent}
          config={config}
          onFirstSectionReady={onFirstSectionReady}
          skipSections={skipSections}
        />
      </LandingPageContentProvider>

      {/* Footer - hidden in debug section mode UNLESS debugging the footer itself, or if in skipSections */}
      {config.layout?.footer &&
        resolvedContent.footer &&
        (!debugSection || debugSection === "footer") &&
        !skipSections.includes("footer") && (
          <Suspense fallback={null}>
            <LazyFooter data={resolvedContent.footer} config={config} />
          </Suspense>
        )}

      {/* GenLP Badge */}
      <GenLPBadge />
    </div>
  );
};

export interface LandingPageRendererProps {
  config?: SiteConfig;
  content: any;
  availableLanguages?: string[];
  defaultLanguage?: Language;
  onLanguageChange?: (language: string) => void;
  className?: string;
  style?: React.CSSProperties;
  enableTheme?: boolean;
  enableDarkMode?: boolean;
  onDarkModeChange?: (isDark: boolean) => void;
  showDarkModeIndicator?: boolean;
  preventGlobalDarkMode?: boolean; // Prevent applying dark class to document root
  children?: React.ReactNode;
  onFirstSectionReady?: () => void;
  isPreviewMode?: boolean;
  debugSection?: string | null; // When set, render only this single section (for debugging)
  skipSections?: string[]; // Sections to skip (rendered elsewhere, e.g., server-side)
}

const LandingPageRenderer: React.FC<LandingPageRendererProps> = ({
  config: propConfig,
  content,
  availableLanguages = ["en"],
  defaultLanguage = "en",
  onLanguageChange,
  className,
  style,
  enableTheme = false,
  enableDarkMode = false,
  onDarkModeChange,
  showDarkModeIndicator = false,
  preventGlobalDarkMode = false,
  children,
  onFirstSectionReady,
  isPreviewMode = false,
  debugSection,
  skipSections = [],
}) => {
  const config = useMemo(() => {
    if (propConfig && Object.keys(propConfig).length > 0) {
      return { ...defaultConfig, ...propConfig } as SiteConfig;
    }
    return defaultConfig as SiteConfig;
  }, [propConfig]);
  const renderedContent = (
    <SiteConfigProvider config={config}>
      <FontPreloader />
      <LandingPageRendererContent
        config={config}
        content={content}
        availableLanguages={availableLanguages}
        onLanguageChange={onLanguageChange}
        className={className}
        style={style}
        enableTheme={enableTheme}
        enableDarkMode={enableDarkMode}
        onDarkModeChange={onDarkModeChange}
        showDarkModeIndicator={showDarkModeIndicator}
        preventGlobalDarkMode={preventGlobalDarkMode}
        onFirstSectionReady={onFirstSectionReady}
        debugSection={debugSection}
        skipSections={skipSections}
      >
        {children}
      </LandingPageRendererContent>
    </SiteConfigProvider>
  );

  const languageTree = enableTheme ? (
    <LandingPageLanguageProvider defaultLanguage={defaultLanguage}>
      <LandingPageThemeProvider
        config={{
          ...config,
          theme: ensureCompleteTheme(config.theme),
        }}
        isDarkMode={config.theme?.darkMode || false}
        onDarkModeChange={onDarkModeChange}
      >
        <LandingPageDirectionProvider>
          <LandingPageLanguageLoader>
            {renderedContent}
          </LandingPageLanguageLoader>
        </LandingPageDirectionProvider>
      </LandingPageThemeProvider>
    </LandingPageLanguageProvider>
  ) : (
    <LandingPageLanguageProvider defaultLanguage={defaultLanguage}>
      <LandingPageDirectionProvider>
        <LandingPageLanguageLoader>{renderedContent}</LandingPageLanguageLoader>
      </LandingPageDirectionProvider>
    </LandingPageLanguageProvider>
  );

  return (
    <PreviewModeProvider value={isPreviewMode}>
      {languageTree}
    </PreviewModeProvider>
  );
};

export default LandingPageRenderer;
